import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, RefreshCw, ArrowLeftRight, Bot, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { dateLabel } from "./styles";

const WHO_STYLE = {
  "ARIA": { cls: "text-[var(--aria-primary)] border-[var(--aria-primary)]/40 bg-[var(--aria-primary)]/10", Icon: Bot },
  "Claude / MCP": { cls: "text-amber-400 border-amber-400/40 bg-amber-400/10", Icon: Sparkles },
};

export const HandoffDigest = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/project-connections/handoffs/recent"); setItems(data); }
    catch { setItems([]); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="aria-card p-5 mb-6" data-testid="handoff-digest-section">
      <div className="flex justify-between items-center gap-3 mb-1">
        <h2 className="text-base font-medium flex items-center gap-2"><History size={16} className="text-[var(--aria-primary)]" /> Recent shared handoffs</h2>
        <button onClick={load} disabled={busy} data-testid="handoff-digest-refresh"
          className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="text-xs text-[var(--aria-text-muted)] mb-4">The latest context ARIA and your Claude project have handed off to each other, newest first.</p>

      {items === null ? (
        <div className="text-sm text-[var(--aria-text-muted)] py-2">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[var(--aria-text-muted)] py-3 flex items-center gap-2" data-testid="handoff-digest-empty">
          <ArrowLeftRight size={14} /> No handoffs yet. Save one from an Ask ARIA answer, or let your Claude project write one via MCP.
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="handoff-digest-list">
          {items.map((h) => {
            const style = WHO_STYLE[h.who] || { cls: "text-[var(--aria-text-muted)] border-[var(--aria-border)]", Icon: ArrowLeftRight };
            const Icon = style.Icon;
            return (
              <li key={h.document_id} className="rounded-lg border border-[var(--aria-border)] p-3" data-testid={`handoff-item-${h.document_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${style.cls}`}>
                      <Icon size={11} /> {h.who}
                    </span>
                    <span className="text-sm font-medium truncate">{h.title}</span>
                  </div>
                  <span className="text-[11px] text-[var(--aria-text-muted)] shrink-0">{dateLabel(h.timestamp)}</span>
                </div>
                {h.summary && <p className="text-xs text-[var(--aria-text-muted)] leading-snug line-clamp-2">{h.summary}</p>}
                <div className="flex items-center justify-between gap-2 mt-2">
                  <span className="text-[11px] text-[var(--aria-text-muted)] truncate">in {h.project_name}</span>
                  <Link to={`/app/project-context/${h.project_id}`} data-testid={`handoff-view-${h.document_id}`}
                    className="text-[11px] text-[var(--aria-primary)] hover:underline shrink-0">View context</Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
